import React, { useMemo } from 'react';
import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Accordion } from 'components/Accordion';
import { QuestionEvaluationResult } from 'generated-graphql/types';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { toPercentage } from 'utils/calculations';
import { BoxedLabel } from './BoxedLabel';

export type WorkflowPreferredAnswerEvaluation = {
  requirement: string;
  isMet: boolean;
  evidence?: string | null;
};

export type WorkflowQuestionOutcome = {
  id: string;
  outreachQuestionId?: string | null;
  questionSlug?: string | null;
  questionContent?: string | null;
  answerText?: string | null;
  answerSummary?: string | null;
  evaluationResult: QuestionEvaluationResult;
  score?: number | null;
  preferredAnswerEvaluations?: Array<WorkflowPreferredAnswerEvaluation | null> | null;
  createdAt: string;
};

type Props = {
  questionOutcomes: Array<WorkflowQuestionOutcome | null>;
};

// Terminal results we surface: the question has been answered (PASSED/FAILED)
// or partially met (NEEDS_REVIEW). PENDING/INCOMPLETE/etc are in-flight and
// dropped. We don't display the pass/fail verdict itself — it's routing/logic
// driven and doesn't map to a meaningful "passed the question" in WFB.
const TERMINAL_RESULTS = new Set<QuestionEvaluationResult>([
  QuestionEvaluationResult.Passed,
  QuestionEvaluationResult.Failed,
  QuestionEvaluationResult.NeedsReview
]);

// A question can accumulate several outcomes as it progresses (document
// validation, clarification follow-ups), and the newest record for a question
// can be a non-terminal in-flight state. Keep the newest *terminal* outcome per
// question so a decided verdict isn't hidden by later progress noise.
export const selectLatestTerminalResponses = (
  questionOutcomes: Array<WorkflowQuestionOutcome | null>
): WorkflowQuestionOutcome[] => {
  const latestByQuestion = new Map<string, WorkflowQuestionOutcome>();

  for (const outcome of questionOutcomes) {
    if (!outcome) continue;
    if (!TERMINAL_RESULTS.has(outcome.evaluationResult)) continue;

    const key =
      outcome.outreachQuestionId || outcome.questionSlug || outcome.id;
    const existing = latestByQuestion.get(key);

    if (
      !existing ||
      new Date(outcome.createdAt) > new Date(existing.createdAt)
    ) {
      latestByQuestion.set(key, outcome);
    }
  }

  // Oldest-first, so the list reads top-to-bottom like the conversation history.
  return [...latestByQuestion.values()].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

const requirementIconSx = { fontSize: '16px', mt: '2px' } as const;
const metIconSx: SxProps<Theme> = {
  ...requirementIconSx,
  color: 'text.secondary'
};
const unmetIconSx: SxProps<Theme> = {
  ...requirementIconSx,
  color: 'text.disabled'
};
// Plain object (not SxProps) because BoxedLabel spreads it into its own sx.
const scoreLabelContainerSx = { alignSelf: 'start' } as const;
const dividerSx: SxProps<Theme> = { my: 2 };

// Per-requirement breakdown for preferred-answer questions: which parts of the
// preferred answer the candidate met, so a partial (NEEDS_REVIEW) or passing
// score is explainable rather than just a percentage.
const PreferredAnswerBreakdown: React.FC<{
  evaluations: Array<WorkflowPreferredAnswerEvaluation | null>;
}> = ({ evaluations }) => (
  <Stack gap={0.5}>
    {evaluations.map((evaluation, index) =>
      evaluation ? (
        <Stack key={index} direction="row" gap="6px" alignItems="flex-start">
          {evaluation.isMet ? (
            <CheckIcon sx={metIconSx} />
          ) : (
            <CloseIcon sx={unmetIconSx} />
          )}
          <Typography variant="caption" color="text.secondary">
            {evaluation.requirement}
          </Typography>
        </Stack>
      ) : null
    )}
  </Stack>
);

const WorkflowResponseItem: React.FC<{ outcome: WorkflowQuestionOutcome }> = ({
  outcome
}) => {
  const answer = outcome.answerSummary || outcome.answerText;

  return (
    <Stack gap={0.5}>
      <Typography fontWeight="bold" variant="body2">
        {outcome.questionContent}
      </Typography>
      {answer && <Typography variant="body2">{answer}</Typography>}
      {outcome.score != null && (
        <BoxedLabel
          containerProps={scoreLabelContainerSx}
          value={toPercentage(outcome.score)}
        />
      )}
      {!!outcome.preferredAnswerEvaluations?.length && (
        <PreferredAnswerBreakdown
          evaluations={outcome.preferredAnswerEvaluations}
        />
      )}
    </Stack>
  );
};

// Popp Classic knockout / screening UI isn't meaningful for WFB conversations,
// so show the per-question responses instead.
export const WorkflowResponses: React.FC<Props> = ({ questionOutcomes }) => {
  const { track } = useTracking();

  const responses = useMemo(
    () => selectLatestTerminalResponses(questionOutcomes),
    [questionOutcomes]
  );

  if (!responses.length) {
    return null;
  }

  return (
    <Accordion
      title={<Typography>Responses</Typography>}
      onClick={() => {
        track(TrackingEventName.CampaignDashboardConversationResponsesSummary);
      }}
    >
      <Box>
        {responses.map((outcome, index) => (
          <Box key={outcome.id}>
            <WorkflowResponseItem outcome={outcome} />
            {index !== responses.length - 1 && <Divider sx={dividerSx} />}
          </Box>
        ))}
      </Box>
    </Accordion>
  );
};
